/**
 * Thin station platform set — departure board + Wi-Fi signs + brighter daylight.
 * Used as procedural fallback until Marble splatTransform is proven in-engine.
 */
export function TrainPlatform() {
  return (
    <group>
      {/* concrete platform */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[28, 20]} />
        <meshStandardMaterial color="#8a9098" roughness={0.92} />
      </mesh>
      {/* track bed (darker strip) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 4.2]} receiveShadow>
        <planeGeometry args={[28, 4]} />
        <meshStandardMaterial color="#3a3e44" roughness={0.95} />
      </mesh>
      {/* rails */}
      {[-0.7, 0.7].map((x, i) => (
        <mesh key={i} position={[x, 0.02, 4.2]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.08, 18]} />
          <meshStandardMaterial color="#b0b8c0" metalness={0.7} roughness={0.35} />
        </mesh>
      ))}
      {/* yellow safety line */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 2.4]}>
        <planeGeometry args={[24, 0.2]} />
        <meshStandardMaterial color="#f0d040" roughness={0.75} />
      </mesh>

      {/* canopy */}
      {[-7, -3.5, 0, 3.5, 7].map((x, i) => (
        <mesh key={i} position={[x, 1.7, -1.0]} castShadow>
          <boxGeometry args={[0.2, 3.4, 0.2]} />
          <meshStandardMaterial color="#9aa2aa" metalness={0.35} roughness={0.5} />
        </mesh>
      ))}
      <mesh position={[0, 3.5, -1.0]} castShadow>
        <boxGeometry args={[16, 0.14, 4.0]} />
        <meshStandardMaterial color="#6a727a" metalness={0.25} roughness={0.55} />
      </mesh>
      {/* glass back wall under canopy */}
      <mesh position={[0, 1.6, -2.9]}>
        <boxGeometry args={[14, 2.8, 0.08]} />
        <meshStandardMaterial
          color="#a8c4d8"
          transparent
          opacity={0.45}
          roughness={0.2}
          metalness={0.3}
        />
      </mesh>

      <DepartureBoard />
      <WifiSign x={-3.4} trap={false} />
      <WifiSign x={2.0} trap />
      <Bench />
      <Vending />
      <PeopleSilhouettes />

      <hemisphereLight args={["#d8e4f0", "#6a7078", 0.75]} />
      <directionalLight position={[5, 10, 3]} intensity={1.6} color="#fff8e8" castShadow />
      <pointLight position={[0, 3.0, -0.4]} intensity={1.0} distance={12} color="#e8f0ff" />
      <pointLight position={[-4, 2.5, 1]} intensity={0.5} distance={8} color="#ffe8c0" />
    </group>
  );
}

function DepartureBoard() {
  return (
    <group position={[0, 2.5, -2.55]}>
      <mesh castShadow>
        <boxGeometry args={[4.2, 1.4, 0.14]} />
        <meshStandardMaterial color="#1a1e24" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0, 0.08]}>
        <planeGeometry args={[3.9, 1.15]} />
        <meshStandardMaterial color="#0a2a18" emissive="#0d4a28" emissiveIntensity={0.75} />
      </mesh>
      {/* row strips */}
      {[-0.35, 0, 0.35].map((y, i) => (
        <mesh key={i} position={[0, y, 0.09]}>
          <planeGeometry args={[3.6, 0.12]} />
          <meshStandardMaterial color="#0e3a22" emissive="#1a5a32" emissiveIntensity={0.4} />
        </mesh>
      ))}
    </group>
  );
}

function WifiSign({ x, trap }: { x: number; trap?: boolean }) {
  return (
    <group position={[x, 1.4, -1.6]}>
      <mesh castShadow>
        <boxGeometry args={[0.1, 1.8, 0.1]} />
        <meshStandardMaterial color="#555" />
      </mesh>
      <mesh position={[0, 0.85, 0.08]} castShadow>
        <boxGeometry args={[1.1, 0.55, 0.06]} />
        <meshStandardMaterial color={trap ? "#4a1a1a" : "#1a2a3a"} />
      </mesh>
      <mesh position={[0, 0.85, 0.12]}>
        <planeGeometry args={[0.95, 0.42]} />
        <meshStandardMaterial
          color={trap ? "#6a2828" : "#285878"}
          emissive={trap ? "#502020" : "#204868"}
          emissiveIntensity={0.55}
        />
      </mesh>
    </group>
  );
}

function Bench() {
  return (
    <group position={[-5.2, 0, 0.6]}>
      <mesh position={[0, 0.42, 0]} castShadow>
        <boxGeometry args={[1.8, 0.08, 0.5]} />
        <meshStandardMaterial color="#5a4a38" roughness={0.85} />
      </mesh>
      <mesh position={[0, 0.7, -0.2]} castShadow>
        <boxGeometry args={[1.8, 0.45, 0.08]} />
        <meshStandardMaterial color="#4a3a2a" roughness={0.85} />
      </mesh>
      {[-0.7, 0.7].map((x, i) => (
        <mesh key={i} position={[x, 0.2, 0]}>
          <boxGeometry args={[0.08, 0.4, 0.4]} />
          <meshStandardMaterial color="#3a3a3a" metalness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

function Vending() {
  return (
    <group position={[5.5, 0, -1.2]}>
      <mesh position={[0, 0.95, 0]} castShadow>
        <boxGeometry args={[0.7, 1.9, 0.55]} />
        <meshStandardMaterial color="#c03030" roughness={0.55} metalness={0.2} />
      </mesh>
      <mesh position={[0, 1.1, 0.28]}>
        <planeGeometry args={[0.5, 1.2]} />
        <meshStandardMaterial color="#1a3040" emissive="#203848" emissiveIntensity={0.35} />
      </mesh>
    </group>
  );
}

function PeopleSilhouettes() {
  return (
    <group>
      {[
        [3.2, 1.2],
        [-2.0, 0.8],
        [1.0, 1.5],
      ].map(([x, z], i) => (
        <group key={i} position={[x, 0.95, z]}>
          <mesh castShadow>
            <sphereGeometry args={[0.14, 12, 12]} />
            <meshStandardMaterial color="#2a2826" roughness={0.9} />
          </mesh>
          <mesh position={[0, -0.4, 0]} castShadow>
            <boxGeometry args={[0.35, 0.65, 0.22]} />
            <meshStandardMaterial color={i === 1 ? "#3a4a6a" : "#4a3a3a"} roughness={0.9} />
          </mesh>
        </group>
      ))}
    </group>
  );
}
